import { SmartSortProduct } from '@iheartjane/dm-sdk';
import type { CompareProduct } from '@iheartjane/menu';
import {
  CompareProducts,
  MAX_COMPARE,
  useCompareActions,
} from '@iheartjane/menu';
import { useToast } from '@iheartjane/reefer';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSubmit } from 'react-router';

import type { ProductAddedToBag } from '@jane/bloom/components/compareProducts/compareAddTrackers';
import {
  COMPARE_ROW_ID,
  CompareAddTrackers,
} from '@jane/bloom/components/compareProducts/compareAddTrackers';
import { buildAddToCartFormArgs } from '@jane/bloom/hooks/useAddToCartConfig';
import { useOptimisticBagSizeGetter } from '@jane/bloom/hooks/useBagSize';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackClickedCompareModalProduct } from '@jane/bloom/services/tracking/track';
import { isCompareProductPayload } from '@jane/bloom/viewModels/compareProductPayload';

/**
 * Owns what happens when user adds to bag from the Compare modal.
 *
 * If the add-to-bag can't be completed in place, go to the PDP.
 *
 * Private to `CompareModal`: the `productsAddedToBag` state re-renders whoever
 * calls this, so it belongs in a leaf and not in the menu layout.
 */
function useCompareAddToBag() {
  const { closeModal, setHandlers } = useCompareActions();
  const { cart, shoppingPreferences } = useRootLoaderData();
  const submit = useSubmit();
  const navigate = useNavigate();
  const toast = useToast();

  const [productsAddedToBag, setProductsAddedToBag] = useState<
    ProductAddedToBag[]
  >([]);

  const hasPreviousStoreProducts = cart.storeId
    ? cart.storeId !== shoppingPreferences?.storeId
    : false;

  /** @returns whether the user was actually redirected to PDP. */
  const goToProduct = useCallback(
    (product: CompareProduct) => {
      if (!product.url) return false;
      closeModal();
      navigate(product.url);
      return true;
    },
    [closeModal, navigate]
  );

  const reportNothingToDo = useCallback(() => {
    toast.add({
      label: 'Sorry, we could not add that to your bag.',
      time: 3000,
    });
  }, [toast]);

  const reportModalProductClick = useCallback((product: CompareProduct) => {
    trackClickedCompareModalProduct({
      productBrand: product.brand,
      productId: product.id,
      productKind: product.kind,
      productName: product.name,
    });
  }, []);

  /** Credits the placement that originally surfaced this product, if any. */
  const trackDmClick = useCallback(
    (smartProductJson: string | null, isAddToCart: boolean) => {
      if (!smartProductJson) return;
      try {
        SmartSortProduct.fromJSON(smartProductJson)
          .click({ isAddToCart })
          .catch(() => {});
      } catch {
        // A malformed or outdated serialised instance shouldn't block add-to-bag.
      }
    },
    []
  );

  const handleAddToCart = useCallback(
    (product: CompareProduct) => {
      if (!isCompareProductPayload(product.payload)) {
        if (goToProduct(product)) reportModalProductClick(product);
        else reportNothingToDo();
        return;
      }
      const { defaultWeight, hasMultipleWeights, smartProductJson } =
        product.payload;

      if (hasPreviousStoreProducts || hasMultipleWeights) {
        if (!goToProduct(product)) {
          reportNothingToDo();
          return;
        }
        trackDmClick(smartProductJson, false);
        reportModalProductClick(product);
        return;
      }

      trackDmClick(smartProductJson, true);
      const payload = product.payload;
      setProductsAddedToBag((previous) =>
        previous.some((entry) => entry.productId === product.id)
          ? previous
          : [...previous, { payload, productId: product.id }].slice(
              -MAX_COMPARE
            )
      );
      submit(
        { count: 1, priceId: defaultWeight, productId: product.id },
        buildAddToCartFormArgs({
          productId: product.id,
          rowId: COMPARE_ROW_ID,
        })
      );
      toast.add({ label: 'Added to bag', time: 2500 });
    },
    [
      goToProduct,
      hasPreviousStoreProducts,
      reportModalProductClick,
      reportNothingToDo,
      submit,
      toast,
      trackDmClick,
    ]
  );

  const handleView = useCallback(
    (product: CompareProduct) => {
      if (isCompareProductPayload(product.payload)) {
        trackDmClick(product.payload.smartProductJson, false);
      }
      trackClickedCompareModalProduct({
        productBrand: product.brand,
        productId: product.id,
        productKind: product.kind,
        productName: product.name,
      });
    },
    [trackDmClick]
  );

  useEffect(() => {
    setHandlers({ addToCart: handleAddToCart, view: handleView });
    return () => setHandlers({});
  }, [setHandlers, handleAddToCart, handleView]);

  return { productsAddedToBag };
}

interface CompareModalProps {
  isMobile: boolean;
}

export const CompareModal = ({ isMobile }: CompareModalProps) => {
  const getCountInCart = useOptimisticBagSizeGetter();
  const { productsAddedToBag } = useCompareAddToBag();

  return (
    <>
      <CompareProducts.Modal
        getCountInCart={getCountInCart}
        isMobile={isMobile}
      />
      <CompareAddTrackers productsAddedToBag={productsAddedToBag} />
    </>
  );
};
